import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  TextField,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Schedule,
  Notifications,
} from '@mui/icons-material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const services = [
  { name: 'API', status: 'operational', uptime: '99.99%' },
  { name: 'Dashboard', status: 'operational', uptime: '99.98%' },
  { name: 'AI Engine', status: 'operational', uptime: '99.97%' },
  { name: 'Facebook Integration', status: 'operational', uptime: '99.95%' },
  { name: 'Instagram Integration', status: 'operational', uptime: '99.94%' },
  { name: 'Telegram Integration', status: 'operational', uptime: '99.96%' },
  { name: 'Webhooks', status: 'operational', uptime: '99.99%' },
  { name: 'Data Export', status: 'operational', uptime: '99.98%' },
];

const incidents: Array<{
  date: string;
  title: string;
  status: string;
  description: string;
  time: string;
}> = [];

const uptimeData = [
  { month: 'Mar 2026', uptime: 99.98 },
  { month: 'Feb 2026', uptime: 99.95 },
  { month: 'Jan 2026', uptime: 99.99 },
  { month: 'Dec 2025', uptime: 99.97 },
  { month: 'Nov 2025', uptime: 99.96 },
  { month: 'Oct 2025', uptime: 99.98 },
];

const StatusPage: React.FC = () => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle color="success" />;
      case 'degraded':
        return <Warning color="warning" />;
      case 'outage':
        return <Error color="error" />;
      case 'scheduled':
        return <Schedule color="info" />;
      case 'resolved':
        return <CheckCircle color="success" />;
      default:
        return <CheckCircle color="success" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'outage':
        return 'error';
      case 'scheduled':
        return 'info';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const allOperational = services.every(s => s.status === 'operational');

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero Section */}
      <Box sx={{ pt: 15, pb: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              System Status
            </Typography>
            <Card
              sx={{
                p: 3,
                bgcolor: allOperational ? 'success.main' : 'warning.main',
                color: 'white',
                maxWidth: 500,
                mx: 'auto',
                mt: 4,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 40 }} />
                <Typography variant="h5" fontWeight="bold">
                  {allOperational ? 'All Systems Operational' : 'Some Systems Degraded'}
                </Typography>
              </Box>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Current Status */}
      <Box sx={{ pb: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
            Current Status
          </Typography>
          <Card>
            <List>
              {services.map((service, index) => (
                <React.Fragment key={service.name}>
                  <ListItem sx={{ py: 2 }}>
                    <ListItemIcon>
                      {getStatusIcon(service.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={service.name}
                      secondary={`Uptime: ${service.uptime}`}
                    />
                    <Chip
                      label={service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                      color={getStatusColor(service.status) as any}
                      size="small"
                    />
                  </ListItem>
                  {index < services.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        </Container>
      </Box>

      {/* Uptime History */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
            Uptime History
          </Typography>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              {uptimeData.map((data, index) => (
                <Box key={index} sx={{ textAlign: 'center', minWidth: 100 }}>
                  <Box
                    sx={{
                      height: 80,
                      bgcolor: data.uptime >= 99.9 ? 'success.main' : data.uptime >= 99.5 ? 'warning.main' : 'error.main',
                      borderRadius: 1,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      pb: 1,
                    }}
                  >
                    <Typography variant="body2" color="white" fontWeight="bold">
                      {data.uptime}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {data.month}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 3, display: 'flex', gap: 3, justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'success.main', borderRadius: 0.5 }} />
                <Typography variant="caption">99.9%+ Uptime</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: 0.5 }} />
                <Typography variant="caption">99.5-99.9% Uptime</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: 'error.main', borderRadius: 0.5 }} />
                <Typography variant="caption">&lt;99.5% Uptime</Typography>
              </Box>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* Incidents */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
            Recent Incidents
          </Typography>
          {incidents.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  No incidents reported
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All systems have been running smoothly. We'll post any incidents and scheduled maintenance here.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            incidents.map((incident, index) => (
              <Card key={index} sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {incident.date}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {incident.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
                      color={getStatusColor(incident.status) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {incident.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {incident.time}
                  </Typography>
                </CardContent>
              </Card>
            ))
          )}
        </Container>
      </Box>

      {/* Subscribe */}
      <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
        <Container maxWidth="sm">
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Notifications sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Subscribe to Status Updates
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Get notified when there are incidents or scheduled maintenance.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <TextField
                placeholder="Enter your email"
                variant="outlined"
                fullWidth
                size="small"
              />
              <Button variant="contained">
                Subscribe
              </Button>
            </Box>
          </Card>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default StatusPage;
